import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { ListingTable } from "components/listing";
import { Button } from "components/buttons";
import { Modal } from "components/notifications";
import { ShowHide } from "components/layout";
import { useUserHasAccess } from "hooks/useUserHasAccess";

const defaultArray = [];
const defaultFunction = () => { };

const breakdownHeaders = ["PERIOD", "KPI", "BREACH TYPE", "BREACH %"];

// The small-operator conditions count raw offences rather than breached KPI thresholds, so an alert
// can legitimately arrive with no breaches behind it. Each stream has its own condition.
const smallOperatorConditionKeys = ["SMALL_OPERATOR_TWO_IN_PERIOD", "SMALL_FLEET_TWO_IN_PERIOD"];

// The drivers KPIs count tachograph days, the maintenance KPIs count fleet size, so the
// small-operator provision is worded per stream.
const smallOperatorExplanations = {
    Drivers: "A period reporting fewer than 25 tachograph days is allowed one offence. This period holds more than one, so an alert was raised.",
    Maintenance: "A fleet of fewer than 20 vehicles including trailers is allowed one failure in a period. This period holds more than one, so an alert was raised."
};

// Builds the popup summary line, e.g. "An alert was created because you received a total of 5 yellow
// threshold breaches, these breaches occurred in the following places:", in the singular for one breach.
// When the breaches are a mix of severities the colour word is dropped.
function buildAlertSummaryText(alert) {
    const breachCount = alert.breaches.length;
    const breachTypes = new Set(alert.breaches.map((breach) => breach.breachType));

    const isSingle = breachCount === 1;
    const breachNoun = isSingle ? "threshold breach" : "threshold breaches";

    let typeText = breachNoun;
    if (breachTypes.size === 1) {
        typeText = `${alert.breaches[0].breachType.toLowerCase()} ${breachNoun}`;
    }

    const whereText = isSingle ? "this breach occurred in the following place:" : "these breaches occurred in the following places:";

    return `An alert was created because you received a total of ${breachCount} ${typeText}, ${whereText}`;
}

// A count row (MSI, repeat offenders) carries a count rather than a breach type and percentage.
function buildAlsoReportedRow(reported) {
    const isCount = reported.percentage == null;

    return [
        `Period ${reported.isoPeriod} ${reported.isoYear}`,
        reported.kpiLabel,
        isCount ? "-" : reported.breachType,
        isCount ? `${reported.count}` : `${reported.percentage}%`
    ];
}

export function ErAlerts(
    {
        alerts = defaultArray,
        loading = false,
        onSelectAlert = defaultFunction,
        selectedAlertId = null,
        noDataMessage = "There are no driver alerts for the selected period.",
        stream = "Drivers"
    }
) {
    const userHasAccess = useUserHasAccess();
    // The alert whose trigger-detail popup is open. Local state — the popup is self-contained and
    // does not affect the KPI grid (unlike the eye-icon highlight, which the parent owns).
    const [infoAlert, setInfoAlert] = useState(null);

    if (!userHasAccess("ER:View")) {
        return <React.Fragment />;
    }

    const headers = ["ISO REPORTING PERIOD", "ALERT TRIGGER", "DVSA ALERTED", "ACTIONS"];

    const rows = alerts.map((alert) => {
        const actions = (
            <React.Fragment>
                <Button
                    minimal={true}
                    icon="info-sign"
                    onClick={() => setInfoAlert(alert)}
                    className="er-alert-info"
                />
                <Button
                    minimal={true}
                    icon="eye-open"
                    onClick={() => onSelectAlert(alert)}
                    className={classNames("er-alert-eye", { "er-alert-eye-active": alert.id === selectedAlertId })}
                />
            </React.Fragment>
        );

        return [
            `${alert.isoPeriod}`,
            alert.triggerDescription,
            alert.dvsaAlerted ? "Yes" : "No",
            actions
        ];
    });

    const breachRows = infoAlert != null
        ? infoAlert.breaches.map((breach) => {
            return [
                `Period ${breach.isoPeriod} ${breach.isoYear}`,
                breach.kpiLabel,
                breach.breachType,
                `${breach.percentage}%`
            ];
        })
        : [];

    // Absent while the API is mid-deploy, which reads as nothing else reported.
    const alsoReportedRows = infoAlert != null && infoAlert.alsoReported != null
        ? infoAlert.alsoReported.map(buildAlsoReportedRow)
        : [];

    return (
        <React.Fragment>
            <h2>Alerts</h2>
            <ListingTable
                loadingData={loading}
                data={rows}
                headers={headers}
                totalRecordCount={rows.length}
                pageable={false}
                sortable={false}
                noDataHeading="No alerts"
                noDataMessage={noDataMessage}
            />
            <Modal
                title={infoAlert != null ? `Period ${infoAlert.isoPeriod} Trigger` : ""}
                isOpen={infoAlert != null}
                onClose={() => setInfoAlert(null)}
                size="large"
            >
                <TriggerDetailBody infoAlert={infoAlert} breachRows={breachRows} alsoReportedRows={alsoReportedRows} stream={stream} />
            </Modal>
        </React.Fragment>
    );
}

ErAlerts.propTypes = {
    alerts: PropTypes.array,
    loading: PropTypes.bool,
    onSelectAlert: PropTypes.func,
    selectedAlertId: PropTypes.number,
    noDataMessage: PropTypes.string,
    stream: PropTypes.oneOf(["Drivers", "Maintenance"])
};

// Body of the trigger-detail popup: the summary line plus the breach breakdown table. A small-operator
// alert has no breaches to break down, so it explains the rule that fired instead. The branch is on the
// condition key, not on an empty breach list, because any other condition alerting with no breaches is a
// fault worth surfacing.
function TriggerDetailBody({ infoAlert = null, breachRows = defaultArray, alsoReportedRows = defaultArray, stream = "Drivers" }) {
    if (infoAlert == null) {
        return <React.Fragment />;
    }

    const isSmallOperatorAlert = smallOperatorConditionKeys.includes(infoAlert.triggerCondition);

    let summaryText = smallOperatorExplanations[stream];
    if (!isSmallOperatorAlert) {
        summaryText = buildAlertSummaryText(infoAlert);
    }

    return (
        <React.Fragment>
            <p>{summaryText}</p>
            <ShowHide
                evaluator={!isSmallOperatorAlert}
                show={(
                    <ListingTable
                        loadingData={false}
                        data={breachRows}
                        headers={breakdownHeaders}
                        totalRecordCount={breachRows.length}
                        pageable={false}
                        sortable={false}
                    />
                )}
            />
            <ShowHide
                evaluator={alsoReportedRows.length > 0}
                show={(
                    <React.Fragment>
                        <p className="spacer-top">These did not cause the alert but were also included in the report to DVSA:</p>
                        <ListingTable
                            loadingData={false}
                            data={alsoReportedRows}
                            headers={breakdownHeaders}
                            totalRecordCount={alsoReportedRows.length}
                            pageable={false}
                            sortable={false}
                        />
                    </React.Fragment>
                )}
            />
        </React.Fragment>
    );
}

TriggerDetailBody.propTypes = {
    infoAlert: PropTypes.object,
    breachRows: PropTypes.array,
    alsoReportedRows: PropTypes.array,
    stream: PropTypes.oneOf(["Drivers", "Maintenance"])
};
